import { cloneDeep, uniq } from 'lodash';
import { ChannelAttributeType } from '../../../enums';
import { Category, ChannelAttribute, Item, Pagination } from '../../../models';
import { ChannelFormData } from '../../../pages/channelmanager/channels/channel-editor/ChannelFormData';
import clientUtils from '../../../utils/clientUtils';
import itemUtils from '../../../utils/itemUtils';
import { ChannelForm } from './ChannelForm';
import * as _ from 'lodash';
import { TagPrefixSortInfo } from '../../../models/channel';

export const publicationDateSortOptions = [
  { id: 'asc', name: 'Ascending' },
  { id: 'desc', name: 'Descending' },
];

export const rangeTypeOptions = [
  { id: 'None', name: 'None' },
  { id: 'Current', name: 'Current' },
  { id: 'Prior', name: 'Prior' },
];

export const rangeOptions = [
  { id: 'None', name: 'None', text: '00.00:00:00' },
  { id: '1', name: '1 Day', text: '1.00:00:00' },
  { id: '5', name: '5 Days', text: '5.00:00:00' },
  { id: '7', name: '7 Days', text: '7.00:00:00' },
  { id: '14', name: '14 Days', text: '14.00:00:00' },
  { id: '30', name: '1 Month', text: '30.00:00:00' },
  { id: '60', name: '2 Months', text: '60.00:00:00' },
  { id: '91', name: '3 Months', text: '91.00:00:00' },
  { id: '182', name: '6 Months', text: '182.00:00:00' },
  { id: '365', name: '12 Months', text: '365.00:00:00' },
];
export const randomizedPlaylistOptions = [
  { id: 'none', name: 'None', text: false },
  { id: 'randomized', name: 'Randomized', text: true },
];

export const initialPagination = {
  sortBy: 'publicationDate',
  descending: true,
  rowsPerPage: 100,
  page: 1,
  totalItems: 0,
  pages: 1,
};

export class DynamicLodContentForm {
  // parentForm: ChannelForm = null;

  maxClipDuration: number | null = null; // maximum allowed length of a clip, in seconds. eg. 300 = 5 minutes
  maxPlaylistDuration: number | null = 0;

  // these act as source of truth...attributes is recalculated based on these...
  selectedCategories: Array<string> = []; // array of category.id...<FuelMultiselect> works in terms of this...
  excludedCategories: Array<string> = []; // array of category.id...<FuelMultiselect> works in terms of this...

  selectedTags: Array<string> = [];
  excludedTags: Array<string> = [];

  attributes: Array<ChannelAttribute> = [];

  randomizePlaylist: { id: string; name: string; text: boolean } = randomizedPlaylistOptions.find((o) => o.id === 'none');

  publicationDateSort: { id: string; name: string } = publicationDateSortOptions.find((o) => o.id === 'desc');
  prefixTagsSort: { id: string; name: string } = publicationDateSortOptions.find((o) => o.id === 'asc');
  rangeType: { id: string; name: string } = rangeTypeOptions.find((o) => o.id === 'None');
  range: { id: string; name: string, text:string } = rangeOptions.find((o) => o.id === 'None');

  prefixSortOrder: Array<TagPrefixSortInfo> = [];

  playlistItems: Array<Item> = [];
  totalDuration: number | null = null;
  pagination: Pagination = cloneDeep(initialPagination);
  grandTotal: number = 0;

  static create(parentForm: ChannelForm): DynamicLodContentForm {
    const form = new DynamicLodContentForm();
    // form.parentForm = parentForm;
    return form;
  }

  static createFromData(parentForm: ChannelForm, data: ChannelFormData): DynamicLodContentForm {
    const form = new DynamicLodContentForm();
    // form.parentForm = parentForm;
    form.loadData(data);
    return form;
  }

  private constructor() {}

  loadData(data: ChannelFormData) {
    // console.log('DynamicLodContentForm.loadData', { data });
    const { channel, categories, assets, transitionTemplate } = data;

    if (channel.metaData.channelContentLimits != null) {
      if (channel.metaData.channelContentLimits.maxClipDuration != null) {
        const m = channel.metaData.channelContentLimits.maxClipDuration;
        this.maxClipDuration = clientUtils.convertTimestampToNumber(m);
        if (this.maxClipDuration <= 0) {
          this.maxClipDuration = null;
        }
      }
      if (channel.metaData.channelContentLimits.maxPlaylistDuration != null) {
        const m = channel.metaData.channelContentLimits.maxPlaylistDuration;
        this.maxPlaylistDuration = clientUtils.convertTimestampToNumber(m);
      }
    }

    this.randomizePlaylist = randomizedPlaylistOptions.find((o) => o.text === channel.metaData.randomizePlaylist);

    // TODO: rangeType and range have to be loaded from the channel here once its been made available by the backend...

    const attrs = channel.attributes;
    if (attrs) {
      this.selectedCategories = attrs.cat
        .map((attr) => {
          const category = categories.find((c) => !c.isDeleted && clientUtils.getKey(c.name) === attr.key);
          return category && category.id;
        })
        .filter((cat) => cat);

      this.selectedTags = attrs.tag.map((attr) => {
        return attr.name;
      });
      if (attrs.excludedCats && attrs.excludedCats.length) {
        this.excludedCategories = attrs.excludedCats
          .map((attr) => {
            const category = categories.find((c) => !c.isDeleted && clientUtils.getKey(c.name) === attr.key);
            return category && category.id;
          })
          .filter((cat) => cat);
      }

      if (attrs.excludedTags && attrs.excludedTags.length) {
        this.excludedTags = attrs.excludedTags.map((attr) => {
          return attr.name;
        });
      }
      // need to bring in any existing scores before we run recalcAttributes()...
      const clonedCategoryAttrs = cloneDeep(attrs.cat);
      const clonedTagAttrs = cloneDeep(attrs.tag);
      for (let i = 0; i < clonedTagAttrs.length; i++) {
        const tag = clonedTagAttrs[i];
        tag.isMeta = itemUtils.isMetaTag(tag.name); // this property is not saved to the database
      }
      this.attributes = clonedCategoryAttrs.concat(clonedTagAttrs);

      if (attrs.publicationDateSort) {
        const value = publicationDateSortOptions.find((o) => o.id === attrs.publicationDateSort);
        if (value === undefined) {
          throw new Error(`Received unexpected value for publicationDateSort from the backend: ${attrs.publicationDateSort}`);
        }
        this.publicationDateSort = value;
      }
      if (attrs.rangeDuration) {
        const value = rangeOptions.find((o) => o.text === attrs.rangeDuration);
        if (!value) {
          this.range = { id: 'None', name: 'None', text:'00.00:00:00'  };
        } else {
          this.range = value;
        }
      }
      if (attrs.prefixSortOrder) {
        this.prefixSortOrder = attrs.prefixSortOrder;
      }
    }
  }
}
